import axios from "axios";
import { CREDENTIALS } from "../constants";
import { createRequest, getUserId } from "../utils/hmac-utils";

// Set to false to disable HMAC signing on loyalty API requests
const LOYALTY_HMAC_ENABLED = true;

/** Loyalty API – axios instance for loyalty program endpoints */
const LOYALTY_API = axios.create({
  baseURL: (process.env.REACT_APP_API_ENDPOINT_LOYALTY || "http://localhost:4004").replace(/\/$/, ""),
  timeout: 30000,
});

LOYALTY_API.interceptors.request.use((config) => {
  try {
    const stored = localStorage.getItem(CREDENTIALS);
    if (stored) {
      const creds = JSON.parse(stored);
      const token = creds?.accessToken || creds?.access_token;
      if (token) {
        config.headers.set("Authorization", `Bearer ${token}`);
      }
      const merchantId = creds?.merchantId;
      if (merchantId) {
        config.headers.set("x-brand-id", merchantId);
      }
    }
  } catch {
    // ignore parse errors
  }
  return config;
});

LOYALTY_API.interceptors.request.use(async (config) => {
  config.headers.set('Accept', 'application/json');
  if (LOYALTY_HMAC_ENABLED && config.method && config.method.toLowerCase() !== 'get' && config.data != null) {
    try {
      config.headers.set('X-source', 'MP');
      const userId = getUserId();
      const bodyWithUser = userId ? { ...(config.data as object), staff_id: userId } : config.data;
      const requestPayload = await createRequest(bodyWithUser);
      config.data = requestPayload;
    } catch {
      // proceed with unsigned body if HMAC key is not yet initialized
    }
  }
  return config;
});

LOYALTY_API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response) {
      switch (err?.response?.status) {
        case 401:
          localStorage.clear();
          window.location.href = "/login";
          break;
        case 403:
          return err?.response;
        case 409:
          return err?.response;
        case 400:
          return err?.response;
        case 500:
          return err?.response;
        default:
          break;
      }
    } else {
      console.error("API ERROR:", err);
    }
    throw err;
  }
);

/** Revamp API – axios instance for product catalog / category endpoints */
const REVAMP_API = axios.create({
  baseURL: (process.env.REACT_APP_API_ENDPOINT_REVAMP || "").replace(/\/$/, ""),
  timeout: 30000,
});

REVAMP_API.interceptors.request.use((config) => {
  
  try {
    const stored = localStorage.getItem(CREDENTIALS);
    if (stored) {
      const creds = JSON.parse(stored);
      const token = creds?.accessToken || creds?.access_token;
      if (token) {
        config.headers.set("Authorization", `Bearer ${token}`);
     
      }
    }
  } catch {
    // ignore parse errors
  }
  return config;
});

REVAMP_API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response) {
      switch (err?.response?.status) {
        case 401:
          localStorage.clear();
          window.location.href = "/login";
          break;
        case 403:
        case 409:
        case 400:
        case 500:
          return err?.response;
        default:
          break;
      }
    } else {
      console.error("REVAMP API ERROR:", err);
    }
    throw err;
  }
);

/** Display API – axios instance for digital display endpoints.
 *  No dedicated backend yet — falls back to REVAMP's host + /api/v1 (same service),
 *  matching services/displayApi.ts. REACT_APP_API_ENDPOINT_DISPLAY still wins if set explicitly. */
const REVAMP_BASE_FOR_DISPLAY = (process.env.REACT_APP_API_ENDPOINT_REVAMP || '').replace(/\/$/, '');
const DISPLAY_API = axios.create({
  baseURL: (
    process.env.REACT_APP_API_ENDPOINT_DISPLAY ||
    (REVAMP_BASE_FOR_DISPLAY ? `${REVAMP_BASE_FOR_DISPLAY}/api/v1` : '/proxy-display')
  ).replace(/\/$/, ''),
  timeout: 30000,
});

DISPLAY_API.interceptors.request.use((config) => {
  try {
    const stored = localStorage.getItem(CREDENTIALS);
    if (stored) {
      const creds = JSON.parse(stored);
      const token = creds?.accessToken || creds?.access_token;
      if (token) config.headers.set('Authorization', `Bearer ${token}`);
    }
  } catch {
    // ignore parse errors
  }
  return config;
});

DISPLAY_API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response) {
      switch (err?.response?.status) {
        case 401:
          localStorage.clear();
          window.location.href = '/login';
          break;
        case 400:
        case 403:
        case 409:
        case 500:
          return err?.response;
        default:
          break;
      }
    } else {
      console.error('DISPLAY API ERROR:', err);
    }
    throw err;
  }
);

export { LOYALTY_API, REVAMP_API, DISPLAY_API };
